import type { User } from '../api/types';

/**
 * License write-gate — true when the /api/me license state permits mutating
 * calls. Absent `license` (old/mock sessions) → allow, matching the Go
 * server's own nil→allow posture (Task 10's middleware only blocks when a
 * license IS present and its status isn't ok/grace). `expired`,
 * `cannot_validate`, `misconfigured`, and `clock_skew` all block writes —
 * only `ok`/`grace` (and absent) allow them. Composed (ANDed) into the
 * `writes_enabled` gate helpers below (canCashboxOperate/canCashboxManage/
 * canServiceWOPost/canServiceCatalog) plus the inline write-gate sites in
 * Anticipos.tsx, PaymentApplyPanel.tsx, and AdminUsers.tsx, so no write
 * button covered by one of THOSE gates is left enabled when the license
 * blocks writes. NOT composed into every write-relevant gate in this file,
 * though: canProvisionOperator below deliberately gates on role/permission
 * only (see its own doc comment) — it's not one of the enumerated
 * `writes_enabled` helpers, so don't assume this function reaches it. The
 * server middleware is the real enforcement — this is the UX layer so users
 * see *why* instead of hitting a silent 403 (see LicenseBanner.tsx).
 */
export function licenseAllowsWrites(user: User): boolean {
  const s = user.license?.status;
  return s == null || s === 'ok' || s === 'grace';
}

export function canSeeMargin(user: User): boolean {
  return user.role === 'owner' || user.role === 'manager';
}

/**
 * Apply-payment gate — the CxC payment panel, the standalone /pagos page and
 * the Clientes menu entry all use this one rule: the write platform must be
 * on (writes_enabled) and only owner/accounting post cash. The server
 * enforces the same on /api/payments; this is the UX layer.
 */
export function canApplyPayments(user: User): boolean {
  return user.features?.writes_enabled === true && (user.role === 'owner' || user.role === 'accounting');
}

export function canPickStore(user: User): boolean {
  return user.role === 'owner' || user.role === 'manager';
}

/** Returns null for "all stores", or a sid for a single-store-locked user. */
export function defaultStoreFilter(user: User): number | null {
  if (user.role === 'staff') return user.sid_assigned;
  return null;
}

/**
 * Conteo físico (physical inventory count) gate — mirrors the check
 * InventoryLayout uses to show/hide the "Conteo físico" tab. Pages under
 * /inventory/conteo/* re-check this themselves since a direct URL hit
 * bypasses the tab's conditional rendering.
 */
export function canUseCount(user: User): boolean {
  return !!user.permissions?.includes('physcount:use') || user.role === 'owner';
}

/**
 * Min/max recalculation gate — the /inventory/minmax tab, the page behind it
 * and the per-part panel all ask the same question: owner, or a role the
 * owner delegated inventory:minmax to. paceWriteServ enforces it again on
 * minmax-apply; this is the UX layer.
 *
 * Callers that WRITE compose this with writes_enabled (see PartMinMaxPanel):
 * the permission says who may propose, the platform flag says whether any
 * write is allowed at all.
 */
export function canMinMax(user: User): boolean {
  return user.role === 'owner' || !!user.permissions?.includes('inventory:minmax');
}

/**
 * Postear (apply a conteo's counted deltas to on-hand stock) gate —
 * physcount:post is the narrower posting tier above physcount:use (server
 * default: owner + manager; see auth.RoleDefaults). Owner always has it;
 * everyone else needs the explicit permission.
 */
export function canPostCount(user: User): boolean {
  return !!user.permissions?.includes('physcount:post') || user.role === 'owner';
}

/**
 * Order Reception ("Recepción") gate — receiving:use permission (server
 * default: owner, manager, staff — dock staff scan against a PO). Mirrors
 * canUseCount. Pages under /po/scan/* re-check this themselves since a
 * direct URL hit bypasses any nav's conditional rendering.
 */
export function canUseReceiving(user: User): boolean {
  return !!user.permissions?.includes('receiving:use') || user.role === 'owner';
}

/**
 * Postear recepción (receive a session's scanned lines into the ERP) gate —
 * receiving:post is the narrower posting tier above receiving:use (server
 * default: owner + manager; see auth.RoleDefaults). Owner always has it;
 * everyone else needs the explicit permission. Mirrors canPostCount.
 */
export function canPostReceiving(user: User): boolean {
  return !!user.permissions?.includes('receiving:post') || user.role === 'owner';
}

/**
 * Pacesetter web terminal ("/pacesetter") gate — pacesetter:use permission
 * (server default: owner, manager, counterman — see auth.RoleDefaults in
 * handlers_pacesetter.go/permissions.go). Double-gated with the customer
 * feature flag (features.pacesetter, off unless pacesetter_enabled in
 * paceBI.ini) — a customer that hasn't had the terminal enabled never shows
 * the nav entry even for a role that would otherwise qualify. The WebSocket
 * handshake is the authoritative server-side gate (404/403); this is a UI
 * hint only, same posture as canUseCount/canUseReceiving.
 */
export function canPacesetter(user: User): boolean {
  return (
    !!user.features?.pacesetter &&
    (user.role === 'owner' ||
      user.role === 'manager' ||
      user.role === 'counterman' ||
      (user.permissions ?? []).includes('pacesetter:use'))
  );
}

/**
 * Terminal Users registry ("/admin/terminal-users") gate — owner or manager,
 * matching the server's /api/admin/terminal-users permission gate. This is
 * the PACETTY↔login mapping for the Pacesetter web terminal (see
 * canPacesetter) — a notch below the BI user registry (AdminUsers,
 * owner-only) since store managers reasonably need to onboard/rotate
 * terminal logins without owner involvement.
 */
export function canManageTerminalUsers(user: User): boolean {
  return user.role === 'owner' || user.role === 'manager';
}

/**
 * ERP green-screen operator provisioning ("Operador ERP") gate — mints a
 * Progress _User authenticated by SETUSERID (the account typed at the
 * Pacesetter login prompt), the MOST sensitive write in the Terminal Users
 * console. Mirrors the server's operator:provision permission — owner-only
 * by default, or an explicit grant via permissions. Narrower than
 * canManageTerminalUsers (owner+manager, the console itself) — the caller
 * additionally requires user.features?.writes_enabled, same as the
 * terminal-provision (printer routing) editor.
 */
export function canProvisionOperator(user: User): boolean {
  return user.role === 'owner' || (user.permissions ?? []).includes('operator:provision');
}

/**
 * Digital Vehicle Inspection ("Inspecciones") gate — dvi:view permission
 * (server default: owner, manager, staff — see RoleStaff in
 * auth.roleDefaults/permissions.go; only accounting and counterman lack it
 * by default). Double-gated with the customer feature flag
 * (features.dvi_enabled, off unless dvi_enabled in paceBI.ini) — a customer
 * that hasn't had the module enabled never shows the nav entry/page even for
 * a role that would otherwise qualify. Mirrors canPacesetter: explicit role
 * defaults ∪ a per-user dvi:view override (so an owner can still grant it to
 * accounting/counterman without a code change). Used both by Header.tsx (nav
 * link) and the standalone Inspecciones page (self-gate on direct URL hit).
 */
export function canViewDvi(user: User): boolean {
  return (
    !!user.features?.dvi_enabled &&
    (user.role === 'owner' ||
      user.role === 'manager' ||
      user.role === 'staff' ||
      (user.permissions ?? []).includes('dvi:view'))
  );
}

/**
 * Service Comms ("Comunicaciones") gate — comms:view permission (server
 * default: owner, manager — see RoleManager in auth.roleDefaults/permissions.go;
 * a narrower management surface than DVI, so no staff default). Double-gated
 * with the customer feature flag (features.comms_enabled, off unless
 * comms_enabled in paceBI.ini) — a customer that hasn't had the module
 * enabled never shows the nav entry/page even for a role that would
 * otherwise qualify. Mirrors canViewDvi: explicit role defaults ∪ a per-user
 * comms:view override. Used both by Header.tsx (nav link) and the standalone
 * Comunicaciones page (self-gate on direct URL hit). The narrower
 * comms:manage permission (Escanear ahora / Reenviar) is checked inline by
 * the page itself, same posture as Discrepancias.tsx's canHeal.
 */
export function canComms(user: User): boolean {
  return (
    !!user.features?.comms_enabled &&
    (user.role === 'owner' ||
      user.role === 'manager' ||
      (user.permissions ?? []).includes('comms:view'))
  );
}

/**
 * Cash-box session write gates. Unlike DVI/comms (which use a feature flag),
 * the module's VISIBILITY is driven by the /api/cashbox/status `enabled` probe;
 * these two gate the WRITE actions inside the tab. Double-gated with
 * features.writes_enabled (the master kill-switch) so the buttons stay disabled
 * wherever writes are off — matching the anticipos/price-upload posture.
 *   operate — Abrir / Retiro / Cerrar+contar (cashier; server default owner+manager).
 *   manage  — Conciliar+completar / Ajuste (management; server default owner+manager).
 */
export function canCashboxOperate(user: User): boolean {
  return (
    !!user.features?.writes_enabled &&
    licenseAllowsWrites(user) &&
    (user.role === 'owner' ||
      user.role === 'manager' ||
      (user.permissions ?? []).includes('cashbox:operate'))
  );
}
export function canCashboxManage(user: User): boolean {
  return (
    !!user.features?.writes_enabled &&
    licenseAllowsWrites(user) &&
    (user.role === 'owner' ||
      user.role === 'manager' ||
      (user.permissions ?? []).includes('cashbox:manage'))
  );
}

/**
 * Service WO write-back gates (the "Preparar facturación" workspace). Mirror
 * of the server's triple gate: writes enabled AND the user holds the perm.
 * Owner+manager hold both by role default (recognized by role, since /api/me
 * only emits override perms); an explicit override grants either.
 *   wo-post  — post labor lines to an open WO (POST /api/service/wo-jobs).
 *   catalog  — create job / technician masters (POST /api/service/{job,tech}).
 */
export function canServiceWOPost(user: User): boolean {
  return (
    !!user.features?.writes_enabled &&
    licenseAllowsWrites(user) &&
    (user.role === 'owner' ||
      user.role === 'manager' ||
      (user.permissions ?? []).includes('service:wo-post'))
  );
}
export function canServiceCatalog(user: User): boolean {
  return (
    !!user.features?.writes_enabled &&
    licenseAllowsWrites(user) &&
    (user.role === 'owner' ||
      user.role === 'manager' ||
      (user.permissions ?? []).includes('service:catalog'))
  );
}

/**
 * Email a purchase order (JCD-23) gate — po:email permission (server
 * default: owner + manager, see auth.roleDefaults). No writes_enabled tie:
 * emailing a PO makes no ERP write, same posture as payablesPerms. The
 * server additionally 503s when purchasing_smtp_host is unconfigured — this
 * is UI-only, so a true "unavailable" state still surfaces as a failed send
 * rather than a hidden button.
 */
export function canEmailPO(user: User): boolean {
  return user.role === 'owner' || user.role === 'manager' || (user.permissions ?? []).includes('po:email');
}
